import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Image as ImageIcon, Upload, Target, CreditCard, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const PLACEMENTS = [
  { v: "banner",   l: "🏠 Bannière Home" },
  { v: "sidebar",  l: "📐 Sidebar" },
  { v: "explorer", l: "📜 In-feed Explore" },
  { v: "track",    l: "🎬 Bas Track" },
  { v: "article",  l: "📰 In-article" },
];
const CATS = [
  { v: "", l: "— Toutes —" },
  { v: "music", l: "🎵 Musique" }, { v: "humor", l: "😂 Humour" },
  { v: "cinema", l: "🎬 Cinéma" }, { v: "dance", l: "💃 Danse" },
  { v: "art", l: "🎨 Art" }, { v: "fashion", l: "👗 Mode" },
  { v: "food", l: "🍲 Cuisine" }, { v: "general", l: "🌍 Général" },
];
const COUNTRIES = [
  { v: "", l: "— Tous pays —" },
  { v: "MG", l: "🇲🇬 Madagascar" },
  { v: "FR", l: "🇫🇷 France (diaspora)" },
  { v: "RE", l: "🇷🇪 Réunion" },
  { v: "MU", l: "🇲🇺 Maurice" },
];
const PAYMENT_METHODS = [
  { v: "mvola", l: "📱 Mvola Mobile Money" },
  { v: "virement", l: "🏦 Virement bancaire" },
];

const inp = "w-full rounded-lg bg-white/[0.04] border border-white/10 px-3 py-2.5 text-sm focus:outline-none focus:border-brand-orange/60";

export const CampaignWizardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [packs, setPacks] = useState([]);
  const [busy, setBusy] = useState(false);
  const [adId, setAdId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    pack_key: "",
    title: "",
    advertiser_name: "",
    image_url: "",
    click_url: "",
    placement: "sidebar",
    category: "",
    target_country: "",
    payment_method: "mvola",
  });

  useEffect(() => {
    if (!user) { navigate("/login?next=/annonceur/campagnes/nouvelle"); return; }
    api.get("/me/advertiser").then(({ data }) => {
      if (data?.status !== "approved") { toast.error("Compte annonceur non approuvé"); navigate("/annonceur"); return; }
      setForm((f) => ({ ...f, advertiser_name: data.company || "" }));
    });
    api.get("/rate-card").then(({ data }) => setPacks(data.packs || []));
    // eslint-disable-next-line
  }, [user]);

  const upd = (patch) => setForm((f) => ({ ...f, ...patch }));

  const uploadFile = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image trop lourde (max 5 Mo)"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", "ad");
    try {
      const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      upd({ image_url: data.url });
      toast.success("Image uploadée");
    } catch { toast.error("Échec de l'upload"); }
    finally { setUploading(false); }
  };

  const next = () => {
    if (step === 1 && !form.pack_key) { toast.error("Choisis un pack"); return; }
    if (step === 2) {
      if (!form.title) { toast.error("Titre requis"); return; }
      if (!form.image_url) { toast.error("Image requise"); return; }
      if (!form.click_url || !/^https?:\/\//i.test(form.click_url)) { toast.error("URL de clic invalide"); return; }
    }
    setStep(step + 1);
  };
  const prev = () => setStep(Math.max(1, step - 1));

  const createDraft = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/me/campaigns", form);
      setAdId(data.ad_id);
      return data.ad_id;
    } catch (e) { toast.error(e?.response?.data?.error || "Erreur création"); return null; }
    finally { setBusy(false); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      let id = adId;
      if (!id) id = await createDraft();
      if (!id) return;
      await api.post(`/me/campaigns/${id}/submit`, { payment_method: form.payment_method });
      toast.success("Campagne soumise ! Suis les instructions de paiement par email 📧");
      navigate("/annonceur");
    } catch (e) { toast.error(e?.response?.data?.error || "Erreur"); }
    finally { setBusy(false); }
  };

  const selectedPack = packs.find((p) => p.key === form.pack_key);
  const fmtAr = (n) => Number(n).toLocaleString("fr-FR") + " Ar";

  return (
    <div className="px-4 md:px-12 py-10 max-w-4xl mx-auto pb-32" data-testid="campaign-wizard">
      <Link to="/annonceur" className="inline-flex items-center gap-1.5 text-sm text-white/55 hover:text-white mb-4"><ArrowLeft size={14} /> Retour au dashboard</Link>

      <h1 className="font-display text-3xl md:text-4xl font-black mb-2">Nouvelle campagne</h1>
      <p className="text-white/55 mb-6 text-sm">Étape {step} sur 4</p>

      {/* Stepper */}
      <ol className="flex items-center gap-2 mb-8">
        {[
          { n: 1, l: "Pack",   icon: Sparkles },
          { n: 2, l: "Créatif",icon: ImageIcon },
          { n: 3, l: "Cible",  icon: Target },
          { n: 4, l: "Paiement", icon: CreditCard },
        ].map((s, i, arr) => (
          <li key={s.n} className="flex-1 flex items-center gap-2">
            <div className={`flex flex-col items-center text-center ${step >= s.n ? "text-brand-orange" : "text-white/35"}`}>
              <div className={`w-9 h-9 rounded-full flex items-center justify-center border ${step >= s.n ? "border-brand-orange bg-brand-orange/10" : "border-white/15"}`}>
                {step > s.n ? <Check size={14} /> : <s.icon size={14} />}
              </div>
              <span className="text-[10px] uppercase tracking-widest font-bold mt-1">{s.l}</span>
            </div>
            {i < arr.length - 1 && <div className={`flex-1 h-px ${step > s.n ? "bg-brand-orange" : "bg-white/10"}`} />}
          </li>
        ))}
      </ol>

      {/* STEP 1: PACK */}
      {step === 1 && (
        <div className="space-y-3" data-testid="step-pack">
          {packs.map((p) => (
            <button key={p.key} onClick={() => upd({ pack_key: p.key })}
              className={`w-full text-left rounded-2xl border p-5 transition ${form.pack_key === p.key ? "border-brand-orange bg-brand-orange/10" : "border-white/10 bg-white/[0.02] hover:bg-white/[0.04]"}`}
              data-testid={`pick-pack-${p.key}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-display text-xl font-black">{p.name}</p>
                    {p.badge && <span className="text-[9px] px-1.5 py-0.5 rounded bg-brand-yellow text-brand-dark font-black">{p.badge}</span>}
                  </div>
                  <p className="text-xs text-white/55">{p.desc} · {p.volume_imp.toLocaleString("fr-FR")} impressions garanties</p>
                </div>
                <p className="font-display text-2xl font-black text-brand-yellow tabular-nums">{p.price_ar_fmt} <span className="text-xs text-white/50 font-normal">Ar/mois</span></p>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* STEP 2: CRÉATIF */}
      {step === 2 && (
        <div className="space-y-4" data-testid="step-creative">
          <div>
            <label className="text-[11px] uppercase tracking-widest text-white/55 font-bold block mb-1">Titre de la campagne *</label>
            <input value={form.title} onChange={(e) => upd({ title: e.target.value })} placeholder="Ex : Promo Festival Donia 2026" className={inp} maxLength="120" data-testid="wiz-title" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-white/55 font-bold block mb-1">Visuel * (jpg/png, max 5 Mo)</label>
            {form.image_url ? (
              <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3 flex items-center gap-3">
                <img src={form.image_url} alt="visuel" className="w-24 h-16 object-cover rounded" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-white/60 truncate">{form.image_url}</p>
                </div>
                <button onClick={() => upd({ image_url: "" })} className="text-xs text-brand-red hover:underline">Changer</button>
              </div>
            ) : (
              <label className={`block ${inp} cursor-pointer text-center text-white/55 hover:bg-white/[0.06] py-6`}>
                <Upload size={18} className="mx-auto mb-1" />
                <span className="text-sm">{uploading ? "Upload en cours…" : "Choisir une image"}</span>
                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadFile(e.target.files?.[0])} data-testid="wiz-upload" />
              </label>
            )}
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-white/55 font-bold block mb-1">URL de destination (clic) *</label>
            <input value={form.click_url} onChange={(e) => upd({ click_url: e.target.value })} placeholder="https://www.exemple.com/promo" className={inp} data-testid="wiz-click-url" />
          </div>
        </div>
      )}

      {/* STEP 3: CIBLE */}
      {step === 3 && (
        <div className="space-y-4" data-testid="step-target">
          <div>
            <label className="text-[11px] uppercase tracking-widest text-white/55 font-bold block mb-1">Emplacement</label>
            <select value={form.placement} onChange={(e) => upd({ placement: e.target.value })} className={inp} data-testid="wiz-placement">
              {PLACEMENTS.map((p) => <option key={p.v} value={p.v}>{p.l}</option>)}
            </select>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-widest text-white/55 font-bold block mb-1">Rubrique</label>
              <select value={form.category} onChange={(e) => upd({ category: e.target.value })} className={inp} data-testid="wiz-category">
                {CATS.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest text-white/55 font-bold block mb-1">Pays cible</label>
              <select value={form.target_country} onChange={(e) => upd({ target_country: e.target.value })} className={inp} data-testid="wiz-country">
                {COUNTRIES.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* STEP 4: PAIEMENT */}
      {step === 4 && (
        <div className="space-y-4" data-testid="step-payment">
          <div className="rounded-2xl border border-brand-orange/30 bg-brand-orange/5 p-5">
            <p className="text-[11px] uppercase tracking-widest text-brand-orange font-bold mb-2">Récapitulatif</p>
            <div className="grid md:grid-cols-2 gap-3 text-sm">
              <Info label="Pack" value={selectedPack?.name || form.pack_key} />
              <Info label="Montant" value={selectedPack ? selectedPack.price_ar_fmt + " Ar / mois" : "—"} highlight />
              <Info label="Titre" value={form.title} />
              <Info label="Emplacement" value={PLACEMENTS.find((p) => p.v === form.placement)?.l} />
              <Info label="Rubrique" value={CATS.find((c) => c.v === form.category)?.l || "Toutes"} />
              <Info label="Pays" value={COUNTRIES.find((c) => c.v === form.target_country)?.l || "Tous"} />
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-white/55 font-bold block mb-2">Méthode de paiement</label>
            <div className="grid md:grid-cols-2 gap-2">
              {PAYMENT_METHODS.map((m) => (
                <button key={m.v} onClick={() => upd({ payment_method: m.v })}
                  className={`rounded-xl border p-4 text-left transition ${form.payment_method === m.v ? "border-brand-orange bg-brand-orange/10" : "border-white/10 bg-white/[0.02] hover:bg-white/[0.04]"}`}
                  data-testid={`wiz-payment-${m.v}`}>
                  <p className="font-bold">{m.l}</p>
                  <p className="text-[11px] text-white/55 mt-0.5">{m.v === "mvola" ? "Numéro Mvola fourni par email après soumission" : "RIB envoyé par email après soumission"}</p>
                </button>
              ))}
            </div>
          </div>
          <p className="text-[11px] text-white/50 italic">
            Après soumission, tu recevras un email avec les coordonnées de paiement. Une fois le paiement reçu, ta campagne sera activée sous 24h ouvrées.
          </p>
        </div>
      )}

      {/* NAV */}
      <div className="flex items-center justify-between mt-8 pt-4 border-t border-white/5">
        {step > 1 ? (
          <button onClick={prev} className="btn-ghost inline-flex items-center gap-1 text-sm" data-testid="wiz-prev"><ArrowLeft size={14} /> Précédent</button>
        ) : <span />}
        {step < 4 ? (
          <button onClick={next} className="btn-primary inline-flex items-center gap-1 text-sm" data-testid="wiz-next">Suivant <ArrowRight size={14} /></button>
        ) : (
          <button onClick={submit} disabled={busy} className="btn-primary inline-flex items-center gap-1 text-sm disabled:opacity-50" data-testid="wiz-submit">
            {busy ? "Envoi…" : "Soumettre la campagne"} <Check size={14} />
          </button>
        )}
      </div>
    </div>
  );
};

const Info = ({ label, value, highlight }) => (
  <div>
    <p className="text-[10px] uppercase tracking-widest text-white/45 font-bold">{label}</p>
    <p className={`font-bold ${highlight ? "text-brand-yellow" : "text-white"}`}>{value || "—"}</p>
  </div>
);

export default CampaignWizardPage;
